import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Team = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cacheBuster, setCacheBuster] = useState(Date.now());

  const fetchTeamMembers = async () => {
    try {
      const response = await axios.get(`${API}/team?t=${Date.now()}`);
      setTeamMembers(response.data);
      setCacheBuster(Date.now());
      setLoading(false);
    } catch (err) {
      setError('Failed to load team members. Please try again later.');
      setLoading(false);
    }
  };

  const getMediaUrl = (url) => {
    if (!url) return '';
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}v=${cacheBuster}`;
  };

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 text-xl mb-4">{error}</p>
          <button
            onClick={fetchTeamMembers}
            className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Separate CEO from other members
  const ceo = teamMembers.find(member => member.position.toLowerCase().includes('ceo'));
  const otherMembers = teamMembers.filter(member => !member.position.toLowerCase().includes('ceo'));

  return (
    <div className="min-h-screen">
      {/* Header Section */}
      <section className="gradient-bg text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Our Team</h1>
          <p className="text-xl hindi-text">हमारी टीम</p>
        </div>
      </section>

      {/* CEO Section */}
      {ceo && (
        <section className="section-padding bg-gradient-to-r from-purple-50 via-pink-50 to-blue-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-800 mb-2">Leadership</h2>
              <p className="text-xl text-gray-600 hindi-text">नेतृत्व</p>
            </div>
            <div className="bg-white rounded-xl shadow-2xl p-8 text-center">
              <img
                src={getMediaUrl(ceo.image)}
                alt={ceo.name}
                className="team-member-image mx-auto"
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/300?text=Leadership';
                }}
              />
              <h3 className="text-2xl font-bold text-gray-800 mb-2">{ceo.name}</h3>
              <p className="text-purple-700 font-semibold text-lg mb-4">{ceo.position}</p>
              <div className="mb-4">
                <p className="text-gray-700 mb-2">
                  <strong>Education:</strong> {ceo.education}
                </p>
                <p className="text-gray-700 mb-4">
                  <strong>Experience:</strong> {ceo.experience}
                </p>
              </div>
              <p className="text-gray-600 leading-relaxed">{ceo.description}</p>
            </div>
          </div>
        </section>
      )}

      {/* Other Team Members */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-2">Core Team Members</h2>
            <p className="text-xl text-gray-600 hindi-text">मुख्य टीम सदस्य</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {otherMembers.map((member) => (
              <div key={member.id} className="team-member-card">
                <img
                  src={getMediaUrl(member.image)}
                  alt={member.name}
                  className="team-member-image"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/300?text=Team+Member';
                  }}
                />
                <h3 className="text-xl font-bold text-gray-800 mb-2 text-center">{member.name}</h3>
                <p className="text-purple-700 font-semibold text-center mb-4">{member.position}</p>
                <div className="mb-4 text-sm">
                  <p className="text-gray-700 mb-2">
                    <strong>Education:</strong> {member.education}
                  </p>
                  <p className="text-gray-700 mb-3">
                    <strong>Experience:</strong> {member.experience}
                  </p>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">{member.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Team;
